/**
 * Экспорт текущего вида карты в PNG с врезанной легендой и атрибуцией.
 *
 * Картинку из карты генплана почти всегда куда-то вставляют — в служебную
 * записку, письмо, презентацию. Без легенды такой скриншот бесполезен (цвета
 * зон ничего не значат), а без атрибуции ещё и нарушает ODbL. Поэтому и то и
 * другое дорисовывается прямо в изображение, а не остаётся на совести того,
 * кто вставляет.
 *
 * Требует `preserveDrawingBuffer: true` у карты: без этого WebGL очищает
 * буфер после отрисовки, и canvas отдаёт пустое полотно.
 */

import { MapLibreMap } from "maplibre-gl";
import { ZONE_GROUPS, type ZoneDefinition } from "~/config/zones";

const PADDING = 16;
const SWATCH = 13;
const ROW_HEIGHT = 19;
const LEGEND_WIDTH = 260;
const FONT_TITLE = "600 13px 'Segoe UI', system-ui, sans-serif";
const FONT_ROW = "12px 'Segoe UI', system-ui, sans-serif";
const FONT_SMALL = "11px 'Segoe UI', system-ui, sans-serif";

export interface ExportOptions {
  map: MapLibreMap;
  attribution: string;
  /** Коды зон, которые попали в кадр. Пусто — легенда не рисуется. */
  visibleZoneCodes: ReadonlySet<string>;
  title: string;
}

export async function exportMapAsPng(options: ExportOptions): Promise<Blob> {
  const { map, attribution, visibleZoneCodes, title } = options;

  const source = map.getCanvas();
  const canvas = document.createElement("canvas");
  canvas.width = source.width;
  canvas.height = source.height;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Браузер не дал 2D-контекст для сборки изображения");

  ctx.drawImage(source, 0, 0);

  // Холст MapLibre — в физических пикселях, а легенда и атрибуция размечены
  // в CSS-пикселях (шрифты 11–13px, ширина врезки 260px). На экране с
  // devicePixelRatio 2 врезка вышла бы вдвое мельче, чем на самом экране,
  // на 3 — почти нечитаемой. Приводим систему координат к CSS-пикселям.
  const ratio = source.width / Math.max(1, map.getContainer().clientWidth);
  ctx.save();
  ctx.scale(ratio, ratio);

  const cssWidth = canvas.width / ratio;
  const cssHeight = canvas.height / ratio;

  const zones = collectZones(visibleZoneCodes);
  if (zones.length > 0) {
    drawLegend(ctx, zones, title, cssHeight);
  }
  drawAttribution(ctx, attribution, cssWidth, cssHeight);
  ctx.restore();

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Не удалось собрать PNG из холста карты"));
    }, "image/png");
  });
}

function collectZones(codes: ReadonlySet<string>): { group: string; zones: ZoneDefinition[] }[] {
  if (codes.size === 0) return [];
  return ZONE_GROUPS.map((group) => ({
    group: group.title,
    zones: group.zones.filter((zone) => codes.has(zone.code)),
  })).filter((entry) => entry.zones.length > 0);
}

function drawLegend(
  ctx: CanvasRenderingContext2D,
  groups: { group: string; zones: ZoneDefinition[] }[],
  title: string,
  canvasHeight: number,
): void {
  // На низком холсте (телефон в ландшафте) полная легенда не помещается и
  // нижние строки просто ушли бы за край. Ограничиваем список и честно
  // сообщаем, сколько зон не показано.
  const available = canvasHeight - PADDING * 2 - 22 - ROW_HEIGHT;
  const maxRows = Math.max(3, Math.floor(available / ROW_HEIGHT));

  let budget = maxRows;
  const shown: { group: string; zones: ZoneDefinition[] }[] = [];
  let hidden = 0;
  for (const entry of groups) {
    if (budget <= 1) {
      hidden += entry.zones.length;
      continue;
    }
    const take = Math.min(entry.zones.length, budget - 1);
    if (take > 0) shown.push({ group: entry.group, zones: entry.zones.slice(0, take) });
    hidden += entry.zones.length - take;
    budget -= take + 1;
  }
  groups = shown;

  const rows = groups.reduce((sum, entry) => sum + entry.zones.length + 1, 0) + (hidden > 0 ? 1 : 0);
  const height = PADDING * 2 + 22 + rows * ROW_HEIGHT;
  const top = Math.max(PADDING, canvasHeight - height - PADDING);

  ctx.save();

  ctx.fillStyle = "rgba(255, 255, 255, 0.94)";
  ctx.strokeStyle = "rgba(0, 0, 0, 0.14)";
  ctx.lineWidth = 1;
  roundRect(ctx, PADDING, top, LEGEND_WIDTH, height, 8);
  ctx.fill();
  ctx.stroke();

  let y = top + PADDING + 12;
  ctx.fillStyle = "#1d2125";
  ctx.font = FONT_TITLE;
  ctx.fillText(title, PADDING + 12, y);
  y += 22;

  for (const entry of groups) {
    ctx.font = FONT_SMALL;
    ctx.fillStyle = "#5c6773";
    ctx.fillText(entry.group.toUpperCase(), PADDING + 12, y);
    y += ROW_HEIGHT;

    for (const zone of entry.zones) {
      ctx.fillStyle = zone.color;
      ctx.strokeStyle = "rgba(0, 0, 0, 0.2)";
      roundRect(ctx, PADDING + 12, y - SWATCH + 2, SWATCH, SWATCH, 3);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = "#1d2125";
      ctx.font = FONT_ROW;
      ctx.fillText(`${zone.code} — ${zone.title}`, PADDING + 12 + SWATCH + 8, y, LEGEND_WIDTH - 56);
      y += ROW_HEIGHT;
    }
  }

  if (hidden > 0) {
    ctx.font = FONT_SMALL;
    ctx.fillStyle = "#5c6773";
    ctx.fillText(`…и ещё зон: ${hidden}`, PADDING + 12, y);
  }

  ctx.restore();
}

function drawAttribution(
  ctx: CanvasRenderingContext2D,
  attribution: string,
  width: number,
  height: number,
): void {
  ctx.save();
  ctx.font = FONT_SMALL;
  const metrics = ctx.measureText(attribution);
  const boxWidth = metrics.width + 16;
  const boxHeight = 20;

  ctx.fillStyle = "rgba(255, 255, 255, 0.88)";
  ctx.fillRect(width - boxWidth - PADDING, height - boxHeight - PADDING, boxWidth, boxHeight);

  ctx.fillStyle = "#3a4149";
  ctx.fillText(attribution, width - boxWidth - PADDING + 8, height - PADDING - 6);
  ctx.restore();
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  ctx.beginPath();
  ctx.roundRect(x, y, width, height, radius);
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Отзыв URL сразу после click() иногда обрывает скачивание в Safari —
  // отдаём браузеру время начать загрузку.
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 4000);
}
